import clsx from 'clsx';
import parse from 'html-react-parser';
import { MouseEvent, useMemo, useState } from 'react';

import { getRandomInt } from '~utils/number';
import { BubbleVariant } from './BubbleVariant';

type BubbleProps = {
  onClick: () => void;
  variant: BubbleVariant;
  color: string;
  count: number;
  label: string;
};

export function Bubble({ count = 0, label, color, onClick, variant }: BubbleProps) {
  const animationNr = useMemo(() => `${getRandomInt(1, 3)}`, []);
  const animationDelay = useMemo(() => `-${getRandomInt(0, 100) / 10}s`, []);
  const [isHovering, setIsHovering] = useState(false);

  function handleClick(evt: MouseEvent<HTMLDivElement>) {
    evt.stopPropagation();
    onClick();
  }

  return (
    <div
      onClick={handleClick}
      className={clsx(
        'topic--bubble',
        'cursor-pointer block flex-shrink-0 relative transition-all duration-500 text-center text-white rounded-full bg-white subpixel-antialiased',
        `animate-orbit${animationNr}`,
        variant === BubbleVariant.Default && 'h-28 w-28',
        variant === BubbleVariant.DefaultWithCount && 'h-30 w-30',
        variant === BubbleVariant.DefaultWithCount && count > 2 && 'h-32 w-32',
        variant === BubbleVariant.Active && 'h-48 w-48 z-10',
        variant === BubbleVariant.ActiveWithCount && 'h-52 w-52 z-10',
      )}
      style={{ animationDelay }}
      onMouseEnter={() => setIsHovering(true)}
      onMouseLeave={() => setIsHovering(false)}
    >
      <div
        className={clsx(
          'topic--bubble-bg',
          `absolute inset-0 rounded-full transition duration-300`,
          variant === BubbleVariant.Default ? 'opacity-40' : 'opacity-100',
          isHovering && 'opacity-60',
        )}
        style={{
          backgroundColor: color,
          boxShadow: `0px 0px ${
            variant === BubbleVariant.Active || variant === BubbleVariant.ActiveWithCount ? 10 : 0
          }px 0px ${color}`,
        }}
      />
      <div
        className={clsx(
          'topic--bubble-content',
          `relative flex items-center justify-center flex-col w-full select-none text-sm h-full`,
        )}
      >
        <div
          className={clsx(
            'topic--bubble-count',
            'absolute font-black text-white transition-all duration-500',
            variant === BubbleVariant.Default && 'hidden',
            variant === BubbleVariant.DefaultWithCount && 'text-3xl pb-3 mb-3',
            variant === BubbleVariant.Active && 'text-6xl opacity-0',
            variant === BubbleVariant.ActiveWithCount && 'text-6xl pb-4 mb-4',
          )}
        >
          {count}
        </div>
        <div
          className={clsx(
            'topic--bubble-label',
            'absolute transition-all subpixel-antialiased',
            variant === BubbleVariant.Default && 'duration-100 font-normal text-metal leading-tight',
            variant === BubbleVariant.DefaultWithCount && 'font-bold text-white text-sm leading-tight pt-3 mt-2',
            variant === BubbleVariant.Active && 'duration-500 font-black text-white text-2xl leading-none',
            variant === BubbleVariant.ActiveWithCount &&
              'duration-500 font-black text-white text-2xl leading-none pt-4 mt-4',
          )}
        >
          {!!label && parse(label)}
        </div>
      </div>
    </div>
  );
}
